import Flex from '@decisiv/ui-components/lib/components/Flex';
import { P } from '@decisiv/ui-components/lib/components/Typography';
import { TypographyColorKeys } from '@decisiv/ui-components/lib/components/Typography/withColors';
import styled from 'styled-components';

import TruncatedText from 'components/TruncatedText';

type Props = {
  textAlign?: 'left' | 'start' | 'center' | 'end' | 'right';
  topLine?: React.ReactNode;
  color?: TypographyColorKeys;
  bottomLine?: string | React.ReactNode;
  truncate?: boolean;
  /**
   * The full, plain-text value of `topLine`. Used as the tooltip content while
   * truncating, which matters when `topLine` is a decorated node (highlighted
   * search terms, a link) rather than a bare string.
   */
  title?: string;
};

const FullWidthFlex = styled(Flex).attrs({ flexDirection: 'column' })`
  width: 100%;
  min-width: 0;
  white-space: nowrap;
  text-align: ${(props: Props) => props.textAlign || 'start'};
` as typeof Flex;

// A decorated line (highlighted search terms, a link) cannot double as the
// tooltip's text, so only a bare value falls back to itself.
const asPlainText = (value: React.ReactNode) =>
  typeof value === 'string' || typeof value === 'number' ? value : undefined;

export default function PlainTextCell({
  bottomLine = '',
  topLine,
  color,
  truncate = false,
  title,
  ...props
}: Props) {
  const text = topLine == null || topLine === '' ? '–' : topLine;

  if (truncate) {
    return (
      <FullWidthFlex {...props}>
        <TruncatedText color={color} text={title ?? asPlainText(text)}>
          {text}
        </TruncatedText>
        <TruncatedText
          color={color}
          size="small"
          shade={1}
          text={asPlainText(bottomLine)}
        >
          {bottomLine}
        </TruncatedText>
      </FullWidthFlex>
    );
  }

  return (
    <FullWidthFlex {...props} title={title}>
      <P color={color} as="span">
        {text}
      </P>
      <P color={color} as="span" size="small" shade={1}>
        {bottomLine}
      </P>
    </FullWidthFlex>
  );
}
